/**
 * "Access hours" section of the plan editor (DNKADM-0315, wiring DNKAPI-0457) — the
 * OPTIONAL weekly window in which members on this plan may book, edited in place on
 * an existing plan (`GET/PUT/DELETE /v2/admin/membership-plans/{id}/access-hours`).
 *
 * Composes the SAME pieces as the location editor (`LocationHoursTab` →
 * `OperatingHoursGrid` + `validateHours.ts` + `CopyDayButton`), driven by the plan id
 * instead of a route location id, with the grid's `onSave` pointed at the per-plan PUT.
 * Validation (overlaps, end-after-start, ≥1 shift on an open day) is therefore the
 * location editor's, byte for byte.
 *
 * Semantics the UI preserves (do not fight them):
 *  - **`days === null` is UNCONFIGURED** — the plan keeps the full member window. It
 *    renders an explicit "Not configured" state with a "Configure access hours"
 *    action, NEVER an empty seven-day grid (which reads as "closed all week").
 *  - **Saving always PUTs seven rows.** An all-closed configured schedule is legitimate
 *    and stays distinct from unconfigured.
 *  - **Remove configuration** (destructive, confirmed) `DELETE`s the cap and returns the
 *    plan to unconfigured — not to all-closed.
 *  - A configured window NARROWS booking against the real bay/staff schedules and can
 *    never widen beyond them; the copy says so and promises no override of resources.
 *
 * Edit mode only: the cap is a sub-resource of an existing plan id. The create-mode
 * dialog shows a one-line note instead (see `PlanDialog`).
 */
import { useCallback, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Typography,
} from '@mui/material';
import { useResource } from '@/hooks/useResource';
import {
  deletePlanAccessHours,
  getPlanAccessHours,
  putPlanAccessHours,
} from '@/api/membershipPlanAccessHours';
import type { WeeklyHoursDay } from '@/api/operatingHours';
import { serverMessage } from '@/api/serverMessage';
import type { ShowToast } from '@/components/toastContext';
import { OperatingHoursGrid } from '@/features/operating-hours/OperatingHoursGrid';

export interface PlanAccessHoursSectionProps {
  /** The existing plan whose cap is edited. */
  planId: string;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
}

/** The seed for a fresh configuration: seven closed days the operator opens as needed. */
function closedWeek(): WeeklyHoursDay[] {
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({ dayOfWeek, isAvailable: false, shifts: [] }));
}

/** Remount key so the grid reseeds from each freshly loaded configured view. */
function daysKey(days: WeeklyHoursDay[]): string {
  return days
    .map(
      (d) =>
        `${d.dayOfWeek}:${
          d.isAvailable ? d.shifts.map((s) => `${s.startTime}-${s.endTime}`).join(',') : 'x'
        }`,
    )
    .join('|');
}

export function PlanAccessHoursSection({ planId, showToast }: PlanAccessHoursSectionProps) {
  const hours = useResource(
    useCallback(() => getPlanAccessHours(planId), [planId]),
    [planId],
  );
  // Unconfigured → "Configure" opens a local, not-yet-saved grid seeded all-closed.
  // A successful Save refetches a configured view, which then takes precedence.
  const [configuring, setConfiguring] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const save = useCallback((days: WeeklyHoursDay[]) => putPlanAccessHours(days, planId), [planId]);

  async function handleRemove() {
    setRemoving(true);
    try {
      await deletePlanAccessHours(planId);
      setConfirmOpen(false);
      setConfiguring(false);
      hours.refetch();
      showToast('Access hours removed — this plan now gets full facility hours', 'success');
    } catch (err) {
      showToast(
        serverMessage(err) ?? 'The access hours couldn’t be removed. Please try again.',
        'error',
      );
    } finally {
      setRemoving(false);
    }
  }

  const loaded = hours.status === 'ready' ? hours.data : null;
  const configured = loaded?.days ?? null;
  // What the grid edits: the saved configuration, else the local seed while configuring.
  const editing = configured ?? (configuring ? closedWeek() : null);

  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 0.75 }}>Access hours</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        Narrows when members on this plan can book. It can’t widen beyond when bays and staff
        are actually scheduled — leave it unconfigured to give the plan full facility hours.
      </Typography>

      {hours.status === 'error' && (
        <Alert
          severity="error"
          sx={{ alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" onClick={() => hours.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn’t load the access hours. Please try again.
        </Alert>
      )}

      {hours.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={96}
          role="status"
          aria-label="Loading access hours"
          sx={{ borderRadius: 3 }}
        />
      )}

      {hours.status === 'ready' && editing === null && (
        <Alert
          severity="info"
          role="status"
          sx={{ alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" onClick={() => setConfiguring(true)}>
              Configure access hours
            </Button>
          }
        >
          Not configured — members on this plan get full facility hours.
        </Alert>
      )}

      {hours.status === 'ready' && editing !== null && (
        <OperatingHoursGrid
          key={`access|${planId}|${configured === null ? 'new' : daysKey(configured)}`}
          days={editing}
          onSave={save}
          refetch={hours.refetch}
          showToast={showToast}
          saveLabel="Save access hours"
          savedMessage="Access hours saved"
          extraActions={
            configured === null ? (
              <Button color="inherit" onClick={() => setConfiguring(false)}>
                Cancel
              </Button>
            ) : (
              <Button color="error" variant="outlined" onClick={() => setConfirmOpen(true)}>
                Remove configuration
              </Button>
            )
          }
        />
      )}

      {confirmOpen && (
        <RemoveConfirmDialog
          removing={removing}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void handleRemove()}
        />
      )}
    </Box>
  );
}

/** Confirm the destructive DELETE — the plan returns to unconfigured (full hours). */
function RemoveConfirmDialog({
  removing,
  onCancel,
  onConfirm,
}: {
  removing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titleId = `${useId()}-title`;
  return (
    <Dialog
      open
      onClose={removing ? undefined : onCancel}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id={titleId}>Remove access hours?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          This removes the plan’s access-hours configuration. Members on this plan will get full
          facility hours again.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={removing} color="inherit">
          Keep hours
        </Button>
        <Button onClick={onConfirm} disabled={removing} color="error" variant="contained">
          {removing ? 'Removing…' : 'Remove configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
